import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import APIClient from "@/APIClient";
import { QueryKey } from "./query-keys";
import type { Ref } from "vue";
import { useToast } from "@/composable/useToast";
import type {
  DiagnosticImage,
  FetchDiagnosticImagesParams,
} from "@/types/diagnostic-images.types";

export const useGetDiagnosticImages = (
  diagnosticId: Ref<number | undefined>,
  params?: Ref<Partial<Omit<FetchDiagnosticImagesParams, "diagnosticId">>>,
) => {
  return useQuery<DiagnosticImage[]>({
    queryKey: [QueryKey.DIAGNOSTIC_IMAGES, { diagnosticId, params }],
    queryFn: async (): Promise<DiagnosticImage[]> => {
      if (!diagnosticId.value) throw new Error("No diagnostic id");
      return await APIClient.getDiagnosticImages({
        diagnosticId: diagnosticId.value,
        ...params?.value,
      });
    },
    enabled: () => !!diagnosticId.value,
  });
};

export const useGetDiagnosticImage = (id: Ref<number | undefined>) => {
  return useQuery({
    queryKey: [QueryKey.DIAGNOSTIC_IMAGES, id],
    queryFn: async () => {
      if (!id.value) throw new Error("No image id");
      return await APIClient.getDiagnosticImage(id.value);
    },
    enabled: () => !!id.value,
  });
};

export const useUpdateDiagnosticImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: { photoNumber?: number | null };
    }) => {
      return await APIClient.updateDiagnosticImage(id, payload);
    },
    onSuccess: () => {
      // La galerie ET les images de rapport (numéro identifiant fusionné) dépendent
      // de cette donnée.
      queryClient.invalidateQueries({ queryKey: [QueryKey.DIAGNOSTIC_IMAGES] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.REPORT_IMAGES] });
    },
  });
};

export const useDeleteDiagnosticImage = (
  diagnosticId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { showWarning, showSuccess } = useToast();

  return useMutation({
    mutationFn: async (id: number) => {
      return await APIClient.deleteDiagnosticImage(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DIAGNOSTIC_IMAGES, { diagnosticId }],
      });
      // Also invalidate report images as they may reference this diagnostic image
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES],
      });
      showSuccess("toast:report-images.delete-success");
    },
    onError: () => {
      showWarning("toast:report-images.delete-error");
    },
  });
};

export const useDeleteManyDiagnosticImages = (
  diagnosticId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { showWarning, showSuccess } = useToast();

  return useMutation({
    mutationFn: async (ids: number[]) => {
      return await APIClient.deleteManyDiagnosticImages(ids);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DIAGNOSTIC_IMAGES, { diagnosticId }],
      });
      // Also invalidate report images
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES],
      });
      if (result.failed.length > 0) {
        showWarning("toast:report-images.delete-many-partial", {
          deleted: result.deleted.length,
          failed: result.failed.length,
        });
      } else {
        showSuccess("toast:report-images.delete-many-success", {
          count: result.deleted.length,
        });
      }
    },
    onError: () => {
      showWarning("toast:report-images.delete-many-error");
    },
  });
};
